console.log('DEBUG: AuthContext.tsx - Module evaluation started');
import React, { createContext, useContext, useState, useEffect } from 'react';
console.log('DEBUG: AuthContext.tsx - React import:', React ? 'present' : 'undefined');
console.log('DEBUG: AuthContext.tsx - createContext import:', typeof createContext !== 'undefined' ? 'present' : 'undefined');

import { authApi } from '../services/api';
import { sanitizeForLog } from '../utils/sanitize';

interface User {
  id: string;
  username: string;
  role: string;
  adminType?: 'super_admin' | 'finance_admin' | 'town_admin' | 'member_admin';
  permissions: string[];
  name?: string;
  townId?: string | null;
  phone?: string;
  memberId?: string;
  isMember?: boolean;
  isAdmin?: boolean;
  isAdminMemberView?: boolean;
  isDevAdminView?: boolean;
  isDevMemberView?: boolean;
  originalUserId?: string;
  originalUserRole?: string;
  originalAdminId?: string;
  originalAdminRole?: string;
}

interface AuthContextType {
  user: User | null;
  login: (credentials: { username: string; password: string; role?: 'admin' | 'member' | 'developer' }) => Promise<boolean>;
  register: (userData: any) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastAuthCheck, setLastAuthCheck] = useState<number>(0);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    // Check for existing session on app load
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (token) {
      // Ensure consistent token storage
      if (!localStorage.getItem('auth_token') && localStorage.getItem('token')) {
        localStorage.setItem('auth_token', token);
      }
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  // Listen for storage changes to handle role switching
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' && e.newValue && e.newValue !== e.oldValue) {
        // Token changed, re-verify
        verifyToken(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const verifyToken = async (token: string) => {
    // Rate limiting: prevent excessive auth verification calls
    const now = Date.now();
    const AUTH_CHECK_INTERVAL = 30000; // 30 seconds
    
    if (now - lastAuthCheck < AUTH_CHECK_INTERVAL && !isVerifying) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Rate limiting auth verification - skipping check');
      }
      setLoading(false);
      return;
    }

    if (isVerifying) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Auth verification already in progress - skipping');
      }
      setLoading(false);
      return;
    }

    setIsVerifying(true);
    setLastAuthCheck(now);

    try {
      // Skip verification for obviously invalid tokens
      if (!token || token.trim() === '' || token === 'null' || token === 'undefined') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        setUser(null);
        setLoading(false);
        setIsVerifying(false);
        return;
      }

      const response = await authApi.verifyToken(token);
      if (response.success && response.data) {
        const userData = response.data.user as any;
        // Enhance user data with computed properties
        const enhancedUser: User = {
          ...userData,
          isMember: userData.role === 'member' || userData.role === 'member_portal_admin',
          isAdmin: ['admin', 'finance_admin', 'town_admin', 'member_admin', 'developer'].includes(userData.role),
          isAdminMemberView: userData.isAdminMemberView || userData.role === 'member_portal_admin',
          isDevAdminView: userData.isDevAdminView || (userData.originalUserRole === 'developer' && userData.role !== 'developer'),
          isDevMemberView: userData.isDevMemberView || false,
          originalUserId: userData.originalUserId,
          originalUserRole: userData.originalUserRole,
          originalAdminId: userData.originalAdminId,
          originalAdminRole: userData.originalAdminRole
        };
        setUser(enhancedUser);

        // Check if token should be refreshed
        if (response.data.shouldRefresh && localStorage.getItem('refresh_token')) {
          try {
            const refreshResponse = await authApi.refreshToken(localStorage.getItem('refresh_token')!);
            if (refreshResponse.success && refreshResponse.data) {
              const refreshData = refreshResponse.data as any;
              localStorage.setItem('auth_token', refreshData.token);
            }
          } catch (refreshError) {
            // Silent refresh failure
          }
        }
      } else {
        // Silently clear invalid/expired tokens without console warnings
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
      }
    } catch (err) {
      // Silently clear invalid tokens without console warnings
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
      setUser(null);
    } finally {
      setLoading(false);
      setIsVerifying(false);
    }
  };

  const login = async (credentials: { username: string; password: string; role?: 'admin' | 'member' | 'developer' }): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const response = await authApi.login(credentials);
      if (process.env.NODE_ENV === 'development') {
        console.log('Login response status:', response.success);
      }
      if (response.success && response.data) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Setting user:', sanitizeForLog(response.data.user.username));
        }

        // Enhance user data with computed properties
        const userData = response.data.user as any;
        const enhancedUser: User = {
          ...userData,
          isMember: userData.role === 'member' || userData.role === 'member_portal_admin',
          isAdmin: ['admin', 'finance_admin', 'town_admin', 'member_admin', 'developer'].includes(userData.role),
          isAdminMemberView: userData.isAdminMemberView || userData.role === 'member_portal_admin',
          isDevAdminView: userData.isDevAdminView || (userData.originalUserRole === 'developer' && userData.role !== 'developer'),
          isDevMemberView: userData.isDevMemberView || false,
          originalUserId: userData.originalUserId,
          originalUserRole: userData.originalUserRole,
          originalAdminId: userData.originalAdminId,
          originalAdminRole: userData.originalAdminRole
        };

        setUser(enhancedUser);
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user_id', response.data.user.id);
        localStorage.setItem('username', response.data.user.username);
        localStorage.setItem('user_role', response.data.user.role);
        localStorage.setItem('user_data', JSON.stringify(enhancedUser));

        if (response.data.refreshToken) {
          localStorage.setItem('refresh_token', response.data.refreshToken);
        }
        return true;
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Login failed:', sanitizeForLog(response.error || 'Unknown error'));
        }
        setError(response.error || 'Login failed');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (process.env.NODE_ENV === 'development') {
        console.log('Login error:', sanitizeForLog(errorMessage));
      }
      setError('Network error. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: any): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const response = await authApi.register(userData);
      if (response.success && response.data) {
        const responseData = response.data as any;
        setUser(responseData.user || responseData); // Handle both nested and flat user data

        if (responseData.token) {
          localStorage.setItem('auth_token', responseData.token);
        }

        if (responseData.refreshToken) {
          localStorage.setItem('refresh_token', responseData.refreshToken);
        }
        return true;
      } else {
        setError(response.error || 'Registration failed');
        return false;
      }
    } catch (err) {
      setError('Network error. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await authApi.logout();
    } catch (err) {
      // Ignore logout errors
    } finally {
      setUser(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token'); // Also remove 'token' for backward compatibility
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_id');
      localStorage.removeItem('username');
      localStorage.removeItem('user_role');
      localStorage.removeItem('user_data');
      localStorage.removeItem('original_auth_token'); // Clean up backup token
      setLoading(false);
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};